import { checkContentHeight } from './checkContentHeight'

/**
 * Go through the nodes of a tree to find a category by it's ID.
 * @param {number} id
 * @param {Object[]} categoryTree
 */
export const getCategoryById = (id, categoryTree) => {
    const found = categoryTree.find((ele) => ele.id === id)

    if (!found) {
        let nextChildren

        if (Array.isArray(categoryTree)) {
            nextChildren = categoryTree.flatMap((cat) =>
                cat.subCategories.length > 0 ? cat.subCategories : []
            )
        }

        if (!nextChildren || nextChildren.length === 0) {
            return undefined
        }

        return getCategoryById(id, nextChildren)
    }

    return found
}

/**
 * Generates a category tree of selected categories
 * @param {Object} state
 * @returns CategoryTree
 */
export const renderCategoryTree = (state) =>
    state.selectedCategories.map((id, depth) => {
        const items = depth === 0
            ? state.categories
            : (getCategoryById(
                state.selectedCategories[depth - 1],
                state.categories
            )?.subCategories)

        let categoriesToRender = items

        if (state.excludedCategories?.length > 0) {
            categoriesToRender = items.filter(({ id }) => !state.excludedCategories.some(excludedId => excludedId === id))
        }

        return categoriesToRender.map(item => ({
            ...item,
            isSelected: item.id === id,
            depth
        }))
    })

/**
 * Generates a flat array of selected categories
 * @param {Object} state
 * @returns CategoryTree
 */
export const renderFlatActiveCategoryTree = (state) =>
    renderCategoryTree(state)
        .flat()
        .filter(({ isSelected }) => isSelected)

/**
 * Add the category ID at the correct depth in the selectedCategories category list.
 * If the category has sub-categories add the next unselected step (-1) to the list.
 * @param {Object} category - the category object containing ID and depth.
 * @param {number[]} selectedCategories - the array containing the selected categories.
 * @returns {number[]} the modified selected categories list.
 */
export const selectCategoryAtDepth = (category, selectedCategories) => {
    selectedCategories[category.depth] = category.id
    // if it's possible to continue to drill down
    if (category.subCategories.length > 0) {
        selectedCategories.push(-1)
    }

    return selectedCategories
}

/**
 * Selects or Deselects a category in a list.
 * Unselected categories are marked as -1.
 * Selected categories are marked with their category ID.
 * @param {Object} category - the category object containing ID and depth.
 * @param {number[]} selectedCategories - the array containing the selected categories.
 * @returns {number[]} the modified selected categories list.
 */
export const toggleCategory = (category, selectedCategories) => {
    // -1 when not selected,
    if (selectedCategories[category.depth] === -1) {
        selectedCategories = selectCategoryAtDepth(category, selectedCategories)
    } else {
        selectedCategories = selectedCategories.splice(0, category.depth + 1)
        // if is already the selected category, deselect it.
        if (selectedCategories[category.depth] === category.id) {
            selectedCategories[category.depth] = -1
        } else {
            selectedCategories = selectCategoryAtDepth(category, selectedCategories)
        }
    }

    return selectedCategories
}

/**
 *
 * @param {HTMLElement} element The element that needs to be in view
 * @param {Object} options Scroll options
 * @returns undefined
 */
export const scrollInView = (element, options = {}) =>
    element.scrollIntoView(options)

/**
 * Delays the execution flow by awaiting a pending promise
 * @param {number} _delay seconds of delay
 * @return A resolved promise
 */
export const delay = (_delay = 0) =>
    new Promise((resolve, reject) => setTimeout(resolve, _delay))

/**
 * Calculates the scroll amount and scrolls left or right.
 * @param {HTMLElement} element scrollable item
 * @param {string} direction 'left' || 'right'
 */
export const scroll = (element, direction) => {
    const container = element.parentElement
    const item = element

    const itemWidth = item.getBoundingClientRect().width
    const style = window.getComputedStyle
        ? getComputedStyle(item, null)
        : item.currentStyle
    const marginLeft = parseFloat(style.marginLeft) || 0
    const marginRight = parseFloat(style.marginRight) || 0
    const outerWidth = itemWidth + marginRight + marginLeft

    return new Promise((resolve, reject) => {
        if (direction === 'right') {
            container.scrollLeft = Math.ceil(container.scrollLeft + outerWidth)
        } else if (direction === 'left') {
            container.scrollLeft = Math.ceil(container.scrollLeft - outerWidth)
        }
        resolve(container)
    })
}

/**
 * Toggles a Faq answer
 * @param {HTMLElement} element dropdown container
 */
export function toggleFaq (element) {
    const isOpening = element.getAttribute('aria-hidden') === 'false'
    const beginVal = isOpening ? 0 : checkContentHeight(element.children)
    const endVal = isOpening ? checkContentHeight(element.children) : 0

    animate(element, 'height', beginVal, endVal, 300)
}

/**
 * Animates property `styleAttr` in element `elm`.
 * @param {HTMLElement} elm
 * @param {string} styleAttr - CSS property to be animated
 * @param {number} beginVal
 * @param {number} endVal
 * @param {number} duration
 */
export const animate = (elm, styleAttr, beginVal, endVal, duration) => {
    const startTime = new Date().getTime()
    let request
    const step = () => {
        const elapsedTime = new Date().getTime() - startTime

        if (elapsedTime >= duration) {
            window.cancelAnimationFrame(request)

            // Remove attribute after animation
            elm.style[styleAttr] = null
        } else {
            elm.style[styleAttr] = `${easeOutQuad(
                elapsedTime,
                beginVal,
                endVal,
                duration
            )}px`
            request = window.requestAnimationFrame(step)
        }
    }

    request = window.requestAnimationFrame(step)
}

/**
 * Returns a property value when being animated at a specific point in time.
 * @param {number} time - current time in milliseconds
 * @param {number} beginVal - initial property value (e.g. height)
 * @param {number} endVal - end property value (e.g. height)
 * @param {number} duration - total animation time
 *
 * @return {number} - computed property at the specific point in time `{time}`
 */
const easeOutQuad = (time, beginVal, endVal, duration) => Math.round(beginVal > endVal
    ? beginVal - (time /= duration) * beginVal
    : -endVal * (time /= duration) * (time - 2) + beginVal)

/**
 * Debounce function to prevent memory issues
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @param {boolean} immediate
 */
export const debounce = (func, wait, immediate) => {
    let timeout

    return function () {
        const context = this
        const args = arguments
        const later = function () {
            timeout = null
            if (!immediate) func.apply(context, args)
        }
        const callNow = immediate && !timeout

        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
        if (callNow) func.apply(context, args)
    }
}
/**
 * Append or replaces styletag
 * @param {HTMLElement} elm The parent element to mount on
 * @param {string} style The CSS inline style string
 * @param {string} id Id for the style tag
 */
export const addStyles = (elm, style, id) => {
    const customStyle = elm.querySelector(`#${id}`) ?? document.createElement('style')

    customStyle.textContent = style
    customStyle.setAttribute('id', id)
    elm.appendChild(customStyle)
}

/**
 * Adds a style tag outside the shadowDOM to use fonts
 * @param {HTMLElement} elm The parent element to mount on
 * @param {string}  fonts The default fonts as CSS inline style string
 */
export const addFonts = (elm, fonts) => {
    const styleNode = document.createElement('style')

    styleNode.innerText = fonts
    elm.insertAdjacentElement('afterbegin', styleNode)
}

/**
 * Merges the config into the state
 * We use assign and don't spread to keep ref to original state, needed for reactivity
 * @param {Proxy} state The reactive state
 * @param {Object} config The config
 * @returns {Proxy} state
 */
export const mergeConfigInState = (state, config) => Object.assign(state, config)

/**
 * Checks the validity of an url
 * @param {string} url
 * @returns {boolean} if its valid or not
 */
export const isValidImageUrl = url => /((http|https):\/\/)(www.)?[a-zA-Z0-9.]{2,256}\.[a-z]{2,6}\b([a-zA-Z0-9.-_~!$&'()*+,;=:@]*)(.*?)\.(jpg|jpeg|png|gif|svg|JPG|JPEG|PNG|GIF|SVG)(\?.*)?$/.test(url)
